import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The exposure of a world: the CameraAttributes of the camera it renders through, else those of its
 * first valid WorldEnvironment, which chooses apart from the Environment (`world_environment.cpp:87-93`).
 * Godot scales the frame by the attributes' exposure normalisation factor, which this code calls the
 * exposure factor. It is the `exposure_multiplier` while the project uses no physical light units
 * (`camera_attributes_storage.cpp:133-138`, `camera_attributes.cpp:56-64`). The camera it elects
 * gives its Environment to the world's chooser too (`WorldEnvironmentScope.tsx`). A world holds one
 * exposure for its lights and its sky; a viewport pass that shares the world draws at its own
 * (`ViewportPassAttributes`).
 */
import { createContext, useContext, useMemo } from 'react';
import { electCameraOfViewport } from '../../godot/scene/camera3d';
import { useEnclosingViewportPath } from '../contexts/EnclosingViewportContext';
import { useOptionalViewportActions } from '../contexts/ViewportActionsContext';
import { useSceneExposure } from '../exposure/sceneExposure';
import { useByValue } from '../hooks/useByValue';
import { sameElements } from '../../utils/sameElements';
import { useRegisterSceneCameraResources, useSceneCameraResources } from '../sceneCameras';
import { useWorldKind } from './WorldKindContext';
import { createWorldGroup, createWorldPublications } from './worldGroup';
const cameraAttributes = createWorldGroup();
export const usePublishCameraAttributes = cameraAttributes.usePublish;
const cameras = createWorldPublications();
/** Puts a Camera3D and what it holds in its world, whether or not the world renders through it. */
export function usePublishCamera(path, current, held) {
    const { attributes, environment } = held;
    useRegisterSceneCameraResources(path, useMemo(() => ({ attributes, environment }), [attributes, environment]));
    const viewport = useEnclosingViewportPath();
    cameras.usePublish(useMemo(() => ({ path, viewport, current, attributes, environment }), [path, viewport, current, attributes, environment]));
}
/**
 * The camera `world` renders through. The root viewport renders through the previewed camera,
 * whichever world holds it (`previewed`), and through none without a preview, since the editor
 * camera holds no attributes. A sub-viewport elects one of its own (`electCameraOfViewport`).
 */
function renderCameraOf(world, viewedPath, previewed, viewportPath, worldCameras) {
    if (world === 'subViewport')
        return electCameraOfViewport(worldCameras, viewportPath);
    return previewed ?? worldCameras.find((camera) => camera.path === viewedPath) ?? null;
}
/** The attributes of a resolution, or null while there are none to read. */
function attributesOf(resolution) {
    return resolution?.status === 'resolved' ? resolution.attributes : null;
}
/**
 * The attributes a render through `camera` draws with: the camera's once they resolve, else the
 * world's. Godot forces a camera's over the scenario's (`renderer_scene_cull.cpp:3562-3565`). While a
 * camera's `.tres` loads, the world's hold, since Godot has no load gap to show.
 */
function drawnAttributes(camera, worldAttributes) {
    return attributesOf(camera?.attributes.status === 'resolved' ? camera.attributes : worldAttributes);
}
/**
 * The CameraAttributes the world renders with, null when there are no valid ones, and Godot's frame
 * is not scaled at all. The factor and the auto exposure are read from the one record.
 */
const CameraAttributesContext = createContext(null);
/** Chooses the world's CameraAttributes and gives its readers the exposure. One per world. */
export function WorldExposureScope({ children }) {
    return (_jsx(cameraAttributes.Provider, { children: _jsx(cameras.Provider, { children: _jsx(RenderCameraProvider, { children: children }) }) }));
}
/**
 * Gathers the CameraAttributes and the cameras of a world that only its shared viewports draw, as a
 * 2D canvas's World3D. Each viewport elects its own camera by its path (`ViewportPassAttributes`).
 */
export function WorldCamerasScope({ children }) {
    return (_jsx(cameraAttributes.Provider, { children: _jsx(cameras.Provider, { children: children }) }));
}
/**
 * Gives the readers below the exposure of the world's own CameraAttributes, for a world that no one
 * camera renders. It reads the chooser of the enclosing `WorldCamerasScope`.
 */
export function WorldAttributesExposure({ children }) {
    const attributes = attributesOf(cameraAttributes.useChosen());
    return _jsx(CameraAttributesContext.Provider, { value: attributes, children: children });
}
/**
 * The exposure of a viewport that shares its parent's world. The world's CameraAttributes group and
 * its cameras are the parent's, keyed by its scenario (`world_environment.cpp:87-93`), so a node
 * inside publishes to the parent's. The scene holds the world's exposure for every viewport that
 * draws it, and each pass draws at its own (`ViewportPassAttributes`).
 */
export function SharedWorldExposureScope({ children }) {
    const factor = attributesOf(cameraAttributes.useChosen())?.exposureMultiplier ?? null;
    useSceneExposure(factor, factor);
    return _jsx(WorldAttributesExposure, { children: children });
}
/** The Environment of the camera the world renders through, null for none. */
const RenderCameraEnvironmentContext = createContext(null);
/**
 * The Environment of the camera the viewport renders through, which replaces its world's once it
 * resolves (`renderer_scene_cull.cpp:3594-3597`). Null when it renders through none.
 */
export function useRenderCameraEnvironment() {
    return useContext(RenderCameraEnvironmentContext);
}
function RenderCameraProvider({ children }) {
    const world = useWorldKind();
    const viewedPath = useOptionalViewportActions()?.previewedCameraPath ?? null;
    const renderCamera = renderCameraOf(world, viewedPath, useSceneCameraResources(viewedPath), useEnclosingViewportPath(), cameras.useAll());
    const worldAttributes = cameraAttributes.useChosen();
    const attributes = drawnAttributes(renderCamera, worldAttributes);
    useSceneExposure(attributes?.exposureMultiplier ?? null, attributesOf(worldAttributes)?.exposureMultiplier ?? null);
    return (_jsx(RenderCameraEnvironmentContext.Provider, { value: renderCamera?.environment ?? null, children: _jsx(CameraAttributesContext.Provider, { value: attributes, children: children }) }));
}
/** The camera the pass of the viewport above draws through, null for none. */
const PassCameraContext = createContext(null);
/**
 * Gives `children`, the tonemap stage of the viewport pass at `path`, the attributes the pass draws
 * with: those of the camera it elects, else the world's own. The world elects the camera once, from
 * what the cameras publish, and the pass draws through it (`usePassCameraPath`). A pass in a shared
 * world draws at the exposure of those attributes, whatever its scene holds (`drawAtExposure`).
 *
 * @canonical The attributes a viewport pass draws with
 */
export function ViewportPassAttributes({ path, children }) {
    const worldCameras = cameras.useAll();
    const worldAttributes = cameraAttributes.useChosen();
    const camera = useMemo(() => electCameraOfViewport(worldCameras, path), [worldCameras, path]);
    const attributes = drawnAttributes(camera, worldAttributes);
    return (_jsx(PassCameraContext.Provider, { value: camera, children: _jsx(CameraAttributesContext.Provider, { value: attributes, children: children }) }));
}
/** The path of the camera the enclosing `ViewportPassAttributes` elects, null for none. */
export function usePassCameraPath() {
    return useContext(PassCameraContext)?.path ?? null;
}
/**
 * The Environment of the camera each viewport pass of the world elects. A viewport with no camera
 * draws nothing of the world. The root viewport's own camera is the view's
 * (`useRenderCameraEnvironment`).
 */
export function usePassCameraEnvironments() {
    const worldCameras = cameras.useAll();
    const elected = useMemo(() => {
        const viewports = new Set(worldCameras.map((camera) => camera.viewport).filter((path) => path !== null));
        return [...viewports].map((path) => electCameraOfViewport(worldCameras, path).environment);
    }, [worldCameras]);
    // A camera that toggles `current` publishes again, and mostly elects the same Environments.
    return useByValue(elected, sameElements);
}
/** The Environment of the camera the enclosing `ViewportPassAttributes` elects, null for none. */
export function usePassCameraEnvironment() {
    return useContext(PassCameraContext)?.environment ?? null;
}
/**
 * The world's exposure factor, or null without CameraAttributes. A lightmap needs the
 * difference: with no attributes it reads 1, not the factor over its baked exposure
 * (`render_forward_clustered.cpp:1222-1229`).
 *
 * @canonical The exposure factor of a world
 */
export function useOptionalExposureFactor() {
    return useContext(CameraAttributesContext)?.exposureMultiplier ?? null;
}
/**
 * The factor a consumer scales by: 1 without CameraAttributes.
 *
 * @canonical The exposure factor of a world
 */
export function useExposureFactor() {
    return useOptionalExposureFactor() ?? 1;
}
/**
 * The auto exposure of the attributes the world renders with, or null without one. The root
 * world's compositor reduces the frame's luminance for it.
 */
export function useAutoExposure() {
    return useContext(CameraAttributesContext)?.autoExposure ?? null;
}
//# sourceMappingURL=worldExposure.js.map